import { useGetTopicDetails, useInteractToNote } from '@/hooks/api/dashboard'
import { useBrowserQuery, useLocalStorage } from '@/hooks/common'
import useAppStore from '@/store/useAppStore'
import { NoteType, SIDE_PANEL_WIDTH, TopicType, TopicTypes } from '@/utils/constants'
import { Box, CircularProgress, Drawer, Typography, useMediaQuery, useTheme } from '@mui/material'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { AlbumPhoto } from '@/types'
import { TopicDetailsContext } from '@/context/TopicDetails'
import TopicDetailsHeader from './TopicDetailsHeader'
import AlbumContent from '@/pages/panels/details/AlbumContent'
import DMContent from '@/pages/panels/details/DMContent'
import RichInput from '@/pages/panels/details/RichInput'
import AlbumInput from '@/pages/panels/details/AlbumInput'
import TopicInteractions from '@/pages/panels/interactions'
import LoadingAnimatedView from '@/components/LoadingAnimatedView'
import { ContentPanel } from '@/pages/__styled'
import colors from '@/styles/colors'
import PinCollectionContent from '@/pages/panels/details/PinCollectionContent'
import { PostGalleryViewer } from './PostGalleryViewer'
import NoDataPlaceholder from '@/components/NoDataPlaceholder'
import { NoDataPlaceholders } from '@/utils/placeholders'

export enum ViewStep {
  PREVIEW = '0',
  DETAILS = '1',
  COMMENTS = '2',
  LIKES = '3',
}

const RICH_INPUT_TOPIC_TYPES = new Set([TopicType.LIST.toString(), TopicType.DISCUSSION.toString()])

// ─── NotesList ────────────────────────────────────────────────────────────────

interface NotesListProps {
  topicID: string
  photos: Record<string, AlbumPhoto>
  isMyTopic: boolean
  onBack?: () => void
}

const NotesList = ({ topicID, photos, isMyTopic, onBack }: NotesListProps) => {
  const { topic } = useContext(TopicDetailsContext)

  if (topic.notes == null) {
    return (
      <Box display='flex' justifyContent='center' pt={4} flex={1}>
        <LoadingAnimatedView showGif={false} />
      </Box>
    )
  }

  const egoNotes = useMemo(() => {
    return topic.notes?.filter((e) => e.egoList.length > 0)
  }, [topic])

  return (
    <Box display='flex' flexDirection='column' height='100%' width='100%' flex={1} minHeight={0}>
      <PostGalleryViewer topic={topic} notes={egoNotes} onBack={onBack} />
    </Box>
  )

  // return (
  //   <Box display='flex' flexDirection='column' height='100%' width='100%' flex={1} minHeight={0}>
  //     {isAlbum ? (
  //       <AlbumContent photos={photos} />
  //     ) : isPinCollection ? (
  //       <PinCollectionContent photos={photos} />
  //     ) : (
  //       <DMContent
  //         topicID={topicID}
  //         topicType={TopicTypes[Number(topicType)]?.title.toLowerCase() ?? ''}
  //         photos={photos}
  //         onClickInteraction={() => {}}
  //       />
  //     )}
  //     {/* <Box p={2}>
  //       {isAlbum && isMyTopic && <AlbumInput />}
  //       {RICH_INPUT_TOPIC_TYPES.has(topicType) && (
  //         <RichInput topicID={topicID} notes={topic.notes} onSubmit={() => {}} />
  //       )}
  //     </Box> */}
  //   </Box>
  // )
}

const TopicDetailsView = () => {
  const query = useBrowserQuery()
  const theme = useTheme()
  const navigate = useNavigate()

  const [showInteractions, setShowInteractions] = useState(false)
  const { pathname } = useLocation()

  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [ewUser] = useLocalStorage('ewUser', null)
  const { topic, onClickBack } = useContext(TopicDetailsContext)
  const topicID = query.get('topicID') || ''
  const noteID = query.get('noteID') || ''
  const isMyTopic = ewUser?.userID == topic.userID
  const topicType = topic.topicType ?? ''
  const isAlbum = topicType === TopicType.ALBUM.toString()
  const isPinCollection = topicType === TopicType.PIN_COLLECTION.toString()

  const photos = useMemo(() => {
    const res: Record<string, AlbumPhoto> = {}
    topic?.photos?.forEach((p) => (res[p.egoID] = p))
    return res
  }, [topic])

  const egoNotes = useMemo(() => {
    return topic.notes?.filter((e) => e.egoList.length > 0)
  }, [topic])

  const onClickCloseInteraction = () => setShowInteractions(false)

  const handleBack = () => {
    if (isMobile && noteID.length > 0) {
      // remove noteID and navigate
      query.delete('noteID')
      navigate(`${pathname}?${query.toString()}`, { replace: true })
    } else {
      onClickBack()
    }
  }

  const sharedHeaderProps = {
    topic,
    onClickBack: handleBack,
    onClickViewInteractions: () => setShowInteractions(true),
    onClickComment: () => {},
    onClickPrivacy: () => {},
    onDeletedTopic: handleBack,
  }

  const notesListProps: NotesListProps = { topicID, photos, isMyTopic }

  if (topic.notes == null) {
    return (
      <Box display='flex' justifyContent='center' pt={4} flex={1}>
        <LoadingAnimatedView showGif={false} />
      </Box>
    )
  }

  return (
    <Box
      display={'flex'}
      flexDirection={'row'}
      sx={{
        display: 'flex',
        height: '100%',
        width: '100%',
        backgroundColor: '#ffffff',
      }}
    >
      {/* ── Desktop ── */}
      <ContentPanel
        sx={{
          display: 'flex',
          height: '100%',
          flex: 1,
          backgroundColor: 'white',
        }}
      >
        {egoNotes?.length > 0 ? (
          <NotesList {...notesListProps} onBack={handleBack} />
        ) : (
          topic.notes != null && (
            <>
              <TopicDetailsHeader {...sharedHeaderProps} />
              <Box
                sx={{
                  display: 'flex',
                  flex: 1,
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: '60px',
                  paddingTop: 4,
                }}
              >
                <NoDataPlaceholder
                  {...(isMyTopic
                    ? isAlbum
                      ? NoDataPlaceholders.emptyAlbumTopicOwner
                      : NoDataPlaceholders.emptyPinCollectionTopicOwner
                    : isAlbum
                      ? NoDataPlaceholders.emptyAlbumTopicVisitor
                      : NoDataPlaceholders.emptyPinCollectionTopicVisitor)}
                />
              </Box>
            </>
          )
        )}
      </ContentPanel>
      {/* {!isMobile && noteID && (
        <ContentPanel
          sx={{
            height: '100%',
            width: SIDE_PANEL_WIDTH,
            background: colors.gradients.interactionPanel,
          }}
        >
          <TopicInteractions
            onClickClose={onClickCloseInteraction}
            showInteractions={showInteractions}
          />
        </ContentPanel>
      )} */}

      {/* ── Mobile ── */}
      {/* <ContentPanel
        sx={{
          display: isMobile ? 'flex' : 'none',
          height: '100%',
          flex: 1,
          backgroundColor: 'white',
          overflow: 'auto',
        }}
      >
        <TopicDetailsHeader {...sharedHeaderProps} />
        {noteID.length > 0 ? (
          <TopicInteractions
            onClickClose={onClickCloseInteraction}
            showInteractions={showInteractions}
          />
        ) : (
          <NotesList {...notesListProps} onBack={handleBack} />
        )}
      </ContentPanel> */}
    </Box>
  )
}

export default TopicDetailsView
